// React imports
import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

// Generic imports
import axios from 'axios';
import _ from 'lodash';
import Papa from 'papaparse';

// MUI Imports
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LinearProgress from '@mui/material/LinearProgress';

function SeaPortsPage(props) {
    const [ waiting, setWaiting ] = useState(false);
    const [ deleting, setDeleting ] = useState(false);
    const [ selectedLetter, setSelectedLetter ] = useState('A');
    const { accesstoken } = useOutletContext();

    const [ seaPorts, setSeaports ] = useState(null);
    const [ csvPreviewOpen, setCsvPreviewOpen ] = useState(false);
    const [ csvPreviewData, setCsvPreviewData ] = useState(null);
    const [ csvErrors, setCsvErrors ] = useState([]);
    const [ uploadingCsv, setUploadingCsv ] = useState(false);
    const [ uploadProgress, setUploadProgress ] = useState(0);
    const [ skippedCount, setSkippedCount ] = useState(0);
    const [ externalUnlocodeCodes, setExternalUnlocodeCodes ] = useState(new Set());

    useEffect(() => {
        if (!accesstoken) return;
        (async() => {
            const [ _seaports, _unlocodeCsv ] = await Promise.all([
                axios(`/api/ports?accesstoken=${accesstoken}`),
                axios('https://monada-storage.s3.us-east-1.amazonaws.com/code-list.csv').catch(() => ({ data: '' }))
            ]);
            
            setSeaports(_(_seaports.data).map(s => ({ type: s.type, ...s.port, dbId:s.id })).value());
            
            // Parse external unlocode CSV
            if (_unlocodeCsv.data) {
                const unlocodeMap = _unlocodeCsv.data.split('\n').map(l => {
                    const parts = l.split(',');
                    if (parts.length >= 3) {
                        const [, codePart1, codePart2] = parts;
                        return codePart1 && codePart2 ? `${codePart1}${codePart2}`.trim() : null;
                    }
                    return null;
                }).filter(code => !!code);
                
                // Skip header row
                if (unlocodeMap.length > 0 && unlocodeMap[0]) {
                    unlocodeMap.shift();
                }
                
                setExternalUnlocodeCodes(new Set(unlocodeMap.map(code => code.toLowerCase())));
            }
        })();
    }, [ accesstoken ]);

    if (!seaPorts) return <Thinking />;

    async function onAdd() {
        const type = document.getElementById('type').value;
        const code = document.getElementById('code').value;
        const portname = document.getElementById('portname').value;
        const lat = document.getElementById('lat').value;
        const lng = document.getElementById('lng').value;

        setWaiting(true);

        try {
            const { data : id } = await axios.post(`/api/ports?accesstoken=${accesstoken}`, {
                type,
                port: { code, portname, lat: parseFloat(lat), lng: parseFloat(lng) }
            });

            setSeaports(sp => ([
                { dbId: id, type, code, portname, lat: parseFloat(lat), lng: parseFloat(lng) },
                ...sp
            ]));

            document.getElementById('code').value = '';
            document.getElementById('portname').value = '';
            document.getElementById('lat').value = '';
            document.getElementById('lng').value = '';
        } catch(e) {
            console.log(e);
        }

        setWaiting(false);
    }

    async function onDelete(id) {
        setDeleting(id);
        try {
            await axios.delete(`/api/ports/${id}?accesstoken=${accesstoken}`);
            setSeaports(sp => sp.filter(s => s.dbId !== id));
        } catch(e) {
            console.log(e);
        }
        setDeleting(false);
    }

    function handleCsvFileSelect(event) {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const csvData = e.target.result;
            parseCsvFile(csvData);
        };
        reader.readAsText(file);
        event.target.value = ''; // Reset file input
    }

    function parseCsvFile(csvData) {
        try {
            const { data: parsedCsv, errors: parseErrors } = Papa.parse(csvData, { 
                header: true,
                skipEmptyLines: true,
                transformHeader: (header) => header.trim().toLowerCase()
            });

            const validatedData = [];
            const validationErrors = [];
            const seen = new Set();
            let skippedExisting = 0;
            const parseErrorMessages = (parseErrors || []).map(e => `Parse error at line ${e.row + 1}: ${e.message}`);

            // Create a set of existing ports for quick lookup (code:type combination)
            const existingPorts = new Set();
            if (seaPorts && seaPorts.length > 0) {
                seaPorts.forEach(port => {
                    const key = `${(port.code || '').toLowerCase()}:${(port.type || '').toLowerCase()}`;
                    existingPorts.add(key);
                });
            }

            parsedCsv.forEach((row, index) => {
                const rowNum = index + 2; // +2 because header is row 1, and index is 0-based
                const errors = [];

                // Normalize column names (handle various formats)
                const type = (row.type || row['port type'] || '').trim().toLowerCase();
                const code = (row.code || row['port code'] || '').trim();
                const portname = (row.portname || row['port name'] || row.name || row['portname'] || '').trim();
                const lat = row.lat || row.latitude || row['lat'] || row['latitude'];
                const lng = row.lng || row.longitude || row['lng'] || row['longitude'] || row.lon || row['lon'];

                // Validate type
                if (!type || (type !== 'sea' && type !== 'unlocode')) {
                    errors.push(`Row ${rowNum}: Type must be 'sea' or 'unlocode'`);
                }

                // Validate code
                if (!code) {
                    errors.push(`Row ${rowNum}: Code is required`);
                }

                // Validate portname
                if (!portname) {
                    errors.push(`Row ${rowNum}: Port name is required`);
                }

                // Validate lat
                const latNum = lat !== undefined && lat !== null && lat !== '' ? parseFloat(lat) : NaN;
                if (isNaN(latNum)) {
                    errors.push(`Row ${rowNum}: Latitude must be a valid number`);
                }

                // Validate lng
                const lngNum = lng !== undefined && lng !== null && lng !== '' ? parseFloat(lng) : NaN;
                if (isNaN(lngNum)) {
                    errors.push(`Row ${rowNum}: Longitude must be a valid number`);
                }

                if (errors.length > 0) {
                    validationErrors.push(...errors);
                } else {
                    // Check for duplicates (code + type combination)
                    const key = `${code.toLowerCase()}:${type}`;
                    const codeLower = code.toLowerCase();
                    
                    // Check if port already exists in the database
                    if (existingPorts.has(key)) {
                        skippedExisting++;
                        validationErrors.push(`Row ${rowNum}: Port already exists in database (code: ${code}, type: ${type})`);
                    } else if (type === 'unlocode' && externalUnlocodeCodes.has(codeLower)) {
                        // Check if unlocode code exists in external code-list.csv
                        skippedExisting++;
                        validationErrors.push(`Row ${rowNum}: Port code already exists in external UN/LOCODE list (code: ${code})`);
                    } else if (seen.has(key)) {
                        // Check for duplicates within the CSV file
                        validationErrors.push(`Row ${rowNum}: Duplicate entry in CSV (code: ${code}, type: ${type})`);
                    } else {
                        seen.add(key);
                        validatedData.push({
                            type: type,
                            code: code,
                            portname: portname,
                            lat: latNum,
                            lng: lngNum
                        });
                    }
                }
            });

            setCsvErrors([...parseErrorMessages, ...validationErrors]);
            setCsvPreviewData(validatedData);
            setSkippedCount(skippedExisting);
            setCsvPreviewOpen(true);
        } catch (error) {
            setCsvErrors([`Error parsing CSV file: ${error.message}`]);
            setCsvPreviewData(null);
            setCsvPreviewOpen(true);
        }
    }

    async function handleBulkUpload() {
        if (!csvPreviewData || csvPreviewData.length === 0) return;

        setUploadingCsv(true);
        setUploadProgress(0);

        const total = csvPreviewData.length;
        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        for (let i = 0; i < csvPreviewData.length; i++) {
            const port = csvPreviewData[i];
            try {
                await axios.post(`/api/ports?accesstoken=${accesstoken}`, {
                    type: port.type,
                    port: {
                        code: port.code,
                        portname: port.portname,
                        lat: port.lat,
                        lng: port.lng
                    }
                });
                successCount++;
            } catch (error) {
                errorCount++;
                errors.push(`Failed to upload ${port.code} (${port.type}): ${error.response?.data?.detail || error.message}`);
            }
            setUploadProgress(((i + 1) / total) * 100);
        }

        setUploadingCsv(false);

        // Refresh the ports list
        const { data: _seaports } = await axios(`/api/ports?accesstoken=${accesstoken}`);
        setSeaports(_(_seaports).map(s => ({ type: s.type, ...s.port, dbId:s.id })).value());

        // Show results
        if (errorCount > 0) {
            setCsvErrors([...errors]);
        } else {
            setCsvErrors([]);
            setCsvPreviewOpen(false);
            setCsvPreviewData(null);
        }
    }

    function handleClosePreview() {
        if (!uploadingCsv) {
            setCsvPreviewOpen(false);
            setCsvPreviewData(null);
            setCsvErrors([]);
            setUploadProgress(0);
            setSkippedCount(0);
        }
    }

    return (
        <Container maxWidth='xl' sx={{ mt: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Sea Ports Management</h2>
                <Box>
                    <input
                        accept='.csv'
                        style={{ display: 'none' }}
                        id='csv-file-input'
                        type='file'
                        onChange={handleCsvFileSelect}
                    />
                    <label htmlFor='csv-file-input'>
                        <Button variant='contained' color='primary' component='span'>
                            Upload CSV
                        </Button>
                    </label>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <ButtonGroup color='primary' aria-label='outlined primary button group'>
                    {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(letter => (
                        <Button
                            key={letter}
                            onClick={() => setSelectedLetter(letter)}
                            variant={selectedLetter === letter ? 'contained' : 'outlined'}
                        >
                            {letter}
                        </Button>
                    ))}
                </ButtonGroup>  
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Database id</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Port Name</TableCell>
                            <TableCell>Lat</TableCell>
                            <TableCell>Lng</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell>
                                <select id='type' defaultValue='sea'>
                                    <option value='sea'>Sea</option>
                                    <option value='unlocode'>Unlocode</option>
                                </select>
                            </TableCell>
                            <TableCell><input id='code' /></TableCell>
                            <TableCell><input id='portname' /></TableCell>
                            <TableCell><input id='lat' /></TableCell>
                            <TableCell><input id='lng' /></TableCell>
                            <TableCell>
                                {waiting && (<CircularProgress />)}
                                {!waiting && (
                                    <Button color='primary' variant='contained' onClick={() => onAdd()}>
                                        + Port
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                        {_.sortBy(seaPorts, s => s.code).filter(s => s.code?.startsWith(selectedLetter)).map(sp => (
                            <TableRow key={sp.dbId}>
                                <TableCell>
                                    <Button disabled={deleting} onClick={() => onDelete(sp.dbId)} color='error' variant='contained'>
                                        {deleting === sp.dbId ? <CircularProgress size={18} /> : 'Delete'}
                                    </Button>
                                </TableCell>
                                <TableCell>{sp.dbId}</TableCell>
                                <TableCell>{_.capitalize(sp.type)}</TableCell>
                                <TableCell>{sp.code}</TableCell>
                                <TableCell>{sp.portname}</TableCell>
                                <TableCell>{sp.lat}</TableCell>
                                <TableCell>{sp.lng}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog 
                open={csvPreviewOpen} 
                onClose={handleClosePreview}
                maxWidth='lg'
                fullWidth
            >
                <DialogTitle>
                    CSV Preview - {csvPreviewData ? `${csvPreviewData.length} valid ports` : 'No valid data'}
                    {skippedCount > 0 && ` (${skippedCount} skipped - already exist)`}
                </DialogTitle>
                <DialogContent>
                    {csvPreviewData && csvPreviewData.length > 0 && (
                        <Alert severity='success' sx={{ mb: 2 }}>
                            {csvPreviewData.length} port{csvPreviewData.length !== 1 ? 's' : ''} ready to upload
                            {skippedCount > 0 && ` • ${skippedCount} port${skippedCount !== 1 ? 's' : ''} skipped (already exist in database)`}
                        </Alert>
                    )}
                    {csvErrors.length > 0 && (
                        <Alert severity='warning' sx={{ mb: 2 }}>
                            <Typography variant='subtitle2' sx={{ mb: 1 }}>Validation Errors ({csvErrors.length}):</Typography>
                            <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                                {csvErrors.map((error, idx) => (
                                    <Typography key={idx} variant='body2' sx={{ mb: 0.5 }}>
                                        • {error}
                                    </Typography>
                                ))}
                            </Box>
                        </Alert>
                    )}
                    
                    {uploadingCsv && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant='body2' sx={{ mb: 1 }}>
                                Uploading ports... {Math.round(uploadProgress)}%
                            </Typography>
                            <LinearProgress variant='determinate' value={uploadProgress} />
                        </Box>
                    )}

                    {csvPreviewData && csvPreviewData.length > 0 && (
                        <TableContainer component={Paper} sx={{ maxHeight: '400px' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Code</TableCell>
                                        <TableCell>Port Name</TableCell>
                                        <TableCell>Lat</TableCell>
                                        <TableCell>Lng</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {csvPreviewData.map((port, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{_.capitalize(port.type)}</TableCell>
                                            <TableCell>{port.code}</TableCell>
                                            <TableCell>{port.portname}</TableCell>
                                            <TableCell>{port.lat}</TableCell>
                                            <TableCell>{port.lng}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {csvPreviewData && csvPreviewData.length === 0 && csvErrors.length > 0 && (
                        <Alert severity='error'>
                            No valid ports found in CSV file. Please fix the errors and try again.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview} disabled={uploadingCsv}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleBulkUpload} 
                        variant='contained' 
                        color='primary'
                        disabled={!csvPreviewData || csvPreviewData.length === 0 || uploadingCsv}
                    >
                        {uploadingCsv ? 'Uploading...' : `Upload ${csvPreviewData?.length || 0} Ports`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default SeaPortsPage;

function Thinking(props) {
    return (
        <Box sx={{ padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
        </Box>
    )
}
